import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box, Typography, Paper, IconButton, Button, Chip, Tooltip,
  TextField, Collapse, CircularProgress, Checkbox, Tabs, Tab,
  InputAdornment, Divider, Badge, Popover, Dialog, DialogTitle,
  DialogContent, DialogActions
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Add as AddIcon,
  DragIndicator as DragIcon,
  CheckCircleOutline as CheckIcon,
  RadioButtonUnchecked as UncheckedIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Assignment as TaskIcon,
  CheckBox as TodoIcon,
  StickyNote2 as NoteIcon,
  Search as SearchIcon,
  AccessTime as ClockIcon,
  Flag as FlagIcon,
  FolderOutlined as ProjectIcon,
  Today as TodayIcon,
  ChevronRight as ChevronRightIcon,
  ChevronLeft as ChevronLeftIcon,
  OpenInNew as OpenInNewIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  CalendarMonth as CalendarIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import toast from 'react-hot-toast';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { useDroppable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { leesJson, meldFout } from '../../utils/apiFout';
import { fetchDayEvents } from '../../utils/graphCalendar';
import PageHeader from '../../ui-components/PageHeader';
import { formatDateISO } from '../../utils/weekUtils';
import { AFGEROND_SET, NIET_PRODUCTIE_SET, isDagstaatTaak } from '../../utils/taakStatus';
import {
  initMicrosoftTodoService,
  getMyTodos,
  completeTask as completeMsTodo,
  reopenTask as reopenMsTodo,
  createTask as createMsTodo, todoDueDate } from '../../utils/microsoftTodoService';
import { biedOngedaanAan, registreerOngedaanSneltoets } from '../../utils/ongedaan';
import OverlegTaak from './OverlegTaak';
import { NietGehouden } from '../Overleg/Verzetten';

// ─── Constants ───────────────────────────────────────────────────────────────

const PRIO_ORDER = ['Prio 1', 'Prio 2', 'Prio 3', 'Prio 4', 'Deadline', 'Geen prio', 'Wacht op feedback'];

const PRIO_COLORS = {
  'Prio 1': '#ef4444', 'Prio 2': '#ef4444', 'Prio 3': '#f59e0b', 'Prio 4': '#f59e0b',
  'Geen prio': '#94a3b8', 'Deadline': '#ef4444', 'Wacht op feedback': '#f97316',
  'Afgerond': '#10b981', 'Opgestuurd': '#8b5cf6', 'Afspraak': '#6b7280',
  'Draaidag': '#f97316', 'Vanallesuurtje': '#3b82f6',
};

const TYPE_ICONS = { 'Montage': '🎬', 'Animatie': '✨', 'Preproductie': '📋', 'Draaidag': '🎥', 'Regie': '🎯', 'Script': '📝' };

// PrioNr values that are calendar/agenda items, not actionable tasks
const SKIP_PRIO_BACKLOG = NIET_PRODUCTIE_SET;

// For today/tomorrow columns: show agenda items but hide Vanallesuurtje
const HIDE_FROM_DAY = new Set(['Vanallesuurtje']);

// ─── Helpers ─────────────────────────────────────────────────────────────────

// Agenda-item (uit Outlook) zonder Airtable-taak — alleen-lezen kaartje zodat de
// dag compleet is, ook als de Outlook→Airtable-sync een afspraak oversloeg.
function CalendarOnlyCard({ item }) {
  const fmt = (iso) => (iso ? iso.slice(11, 16) : '');
  return (
    <Paper elevation={0} sx={{
      p: 1.5, borderRadius: '0.75rem', border: '1px dashed rgba(82,96,117,0.35)',
      backgroundColor: '#fbfcfe', display: 'flex', alignItems: 'center', gap: 1.25,
    }}>
      <Chip label={fmt(item.start)} size="small"
        sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, backgroundColor: '#e0e7ff', color: '#4338ca' }} />
      <Typography variant="body2" sx={{ fontWeight: 500, color: '#4b5563', flex: 1 }} noWrap>
        {item.title}
      </Typography>
      <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.65rem' }}>
        agenda · {fmt(item.start)}–{fmt(item.end)}
      </Typography>
    </Paper>
  );
}

function todayStr() {
  return formatDateISO(new Date());
}

function tomorrowStr() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().split('T')[0];
}

function longDateFor(date) {
  const days = ['zondag', 'maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag'];
  const months = ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus', 'september', 'oktober', 'november', 'december'];
  return `${days[date.getDay()]} ${date.getDate()} ${months[date.getMonth()]}`;
}

function tomorrowLongDate() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return longDateFor(d);
}

function formatDate(d) {
  if (!d) return '';
  const dt = new Date(d);
  const days = ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za'];
  const months = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
  return `${days[dt.getDay()]} ${dt.getDate()} ${months[dt.getMonth()]}`;
}

function longDate() {
  const dt = new Date();
  const days = ['zondag', 'maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag'];
  const months = ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus', 'september', 'oktober', 'november', 'december'];
  return `${days[dt.getDay()]} ${dt.getDate()} ${months[dt.getMonth()]}`;
}

function daysUntil(dateStr) {
  if (!dateStr) return Infinity;
  const target = new Date(dateStr);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);
  return Math.ceil((target - now) / (1000 * 60 * 60 * 24));
}

// Normalize task from various sources
/** Een draaidag of vrije dag die al voorbij is: hoort nergens meer als (achterstallig) werk. */
const isVoorbijeDagstaat = (t, vandaag) => {
  if (!t || !(isDagstaatTaak(t) || isDagstaatTaak(t._raw))) return false;
  const eind = String(t._raw?.fields?.EndDateUitvoerder || t.deadline || t.startDate || '').slice(0, 10);
  return !!eind && eind < vandaag;
};

function mapAirtableTask(rec) {
  const f = rec.fields || {};
  return {
    id: `at-${rec.id}`,
    source: 'airtable',
    title: f.NaamTaak || 'Naamloos',
    isComplete: f.PrioNr === 'Afgerond' || f.PrioNr === 'Opgestuurd' || !!f.Afgerond,
    priority: f.PrioNr || 'Geen prio',
    type: f.Type || null,
    hours: parseFloat(f.UrenWerk) || null,
    // Begintijd uit de gekoppelde Outlook-afspraak (lookup, kan array zijn)
    startTime: (Array.isArray(f['Begintijd (from MeetingsOutlook)'])
      ? f['Begintijd (from MeetingsOutlook)'][0]
      : f['Begintijd (from MeetingsOutlook)']) || null,
    deadline: f.Deadline || f.EndDateUitvoerder || null,
    projectName: f['Prnr en Naam']?.[0] || f.ProjectNummer?.[0] || null,
    projectId: f.Project?.[0] || null,
    startDate: f.StartDateUitvoerder || null,
    notes: f.Opmerking || f.Opmerkingen || null,
    _airtableId: rec.id,
    _raw: rec,
  };
}

// Vorige planning vastleggen vóór een verplaatsing, zodat "Ongedaan maken" exact terug kan.
// item.startDate is leidend (kan al optimistic verschoven zijn); de einddatum komt uit het
// ruwe record zolang dat nog bij die startdatum hoort.
const vorigeDatums = (item) => {
  const f = item?._raw?.fields || {};
  const start = item?.startDate || f.StartDateUitvoerder || null;
  const eind = f.StartDateUitvoerder === start ? (f.EndDateUitvoerder || start) : start;
  return { StartDateUitvoerder: start, EndDateUitvoerder: eind };
};

function mapMsTodo(todo, listId) {
  return {
    id: `ms-${todo.id}`,
    source: 'mstodo',
    title: todo.title || 'Taak',
    isComplete: todo.status === 'completed',
    priority: todo.importance === 'high' ? 'Prio 1' : todo.importance === 'low' ? 'Prio 4' : null,
    type: null,
    hours: null,
    deadline: todoDueDate(todo),
    projectName: null,
    projectId: null,
    startDate: null,
    notes: todo.body?.content || null,
    _msListId: listId,
    _msTodoId: todo.id,
    _raw: todo,
  };
}

function mapActionItem(item) {
  return {
    id: `act-${item.id}`,
    source: 'action',
    title: item.text,
    isComplete: false,
    priority: null,
    type: null,
    hours: null,
    deadline: null,
    projectName: item.projectName || null,
    projectId: item.projectId || null,
    startDate: null,
    notes: null,
    _noteId: item.noteId,
    _noteDate: item.noteDate,
    _raw: item,
  };
}

// ─── Draggable Task Card ─────────────────────────────────────────────────────

function DraggableCard({ item, isOverlay, onToggle, onMoveDay, moveDayDirection, onClickTask, onOpenDatePicker, onMarkSent }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
    data: { item },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
  };

  const prioColor = PRIO_COLORS[item.priority] || '#94a3b8';
  const typeIcon = TYPE_ICONS[item.type] || '';
  const dl = item.deadline ? daysUntil(item.deadline) : null;
  // Draaidag of vrije dag: voorbij is voorbij, nooit te laat (Joris, 3 nov 2026).
  const isOverdue = dl !== null && dl < 0 && !isDagstaatTaak(item);
  const isDueSoon = dl !== null && dl >= 0 && dl <= 2;

  return (
    <Box
      ref={isOverlay ? undefined : setNodeRef}
      style={isOverlay ? undefined : style}
      {...(isOverlay ? {} : attributes)}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1,
        p: 1.5, borderRadius: '0.625rem',
        backgroundColor: item.isComplete ? '#f0fdf4' : '#fff',
        border: `1px solid ${item.isComplete ? '#bbf7d0' : 'rgba(45,51,53,0.06)'}`,
        transition: 'all 0.15s ease',
        '&:hover': { borderColor: '#52607540', boxShadow: '0 2px 8px rgba(45,51,53,0.06)' },
        ...(isOverlay && { boxShadow: '0 12px 32px rgba(45,51,53,0.15)', transform: 'rotate(2deg)' }),
      }}
    >
      {/* Drag handle — only this element activates drag */}
      <Box {...(isOverlay ? {} : listeners)} sx={{ cursor: 'grab', display: 'flex', alignItems: 'center', flexShrink: 0, p: 0.25 }}>
        <DragIcon sx={{ fontSize: 16, color: '#d1d5db' }} />
      </Box>

      {/* Checkbox */}
      <Checkbox
        checked={item.isComplete}
        onClick={() => onToggle?.(item)}
        icon={<UncheckedIcon sx={{ fontSize: 20 }} />}
        checkedIcon={<CheckIcon sx={{ fontSize: 20, color: '#10b981' }} />}
        sx={{ p: 0 }}
      />

      {/* Priority dot */}
      {item.priority && !item.isComplete && (
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: prioColor, flexShrink: 0 }} />
      )}

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          onClick={(e) => onClickTask?.(item, e)}
          sx={{
            fontWeight: 500, fontSize: '0.875rem',
            textDecoration: item.isComplete ? 'line-through' : 'none',
            color: item.isComplete ? '#9ca3af' : '#111827',
            cursor: onClickTask ? 'pointer' : 'default',
            '&:hover': onClickTask ? { color: '#526075', textDecoration: 'underline' } : {},
          }}
        >
          {typeIcon && `${typeIcon} `}{item.title}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.25, alignItems: 'center' }}>
          {item.projectName && (
            <Typography variant="caption" sx={{ color: '#adb3b5', fontSize: '0.7rem' }}>
              {item.projectName}
            </Typography>
          )}
          {item.deadline && (
            <Typography variant="caption" sx={{
              color: isOverdue ? '#ef4444' : isDueSoon ? '#f59e0b' : '#9ca3af',
              fontWeight: isOverdue || isDueSoon ? 600 : 400,
              fontSize: '0.7rem',
            }}>
              {isOverdue ? `${Math.abs(dl)}d te laat` : dl === 0 ? 'Vandaag' : dl === 1 ? 'Morgen' : formatDate(item.deadline)}
            </Typography>
          )}
          {item.startTime && !item.isComplete && (
            <Chip
              label={new Date(item.startTime).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}
              size="small"
              sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, backgroundColor: '#e0e7ff', color: '#4338ca' }}
            />
          )}
          {item.hours != null && !item.isComplete && (
            <Chip
              icon={<ClockIcon sx={{ fontSize: 11 }} />}
              label={`${item.hours}u`}
              size="small"
              sx={{ height: 20, fontSize: '0.65rem', backgroundColor: '#f3f4f6' }}
            />
          )}
        </Box>
        {/* Notes */}
        {item.notes && (
          <Typography variant="caption" sx={{
            color: '#767c7e', fontSize: '0.7rem', mt: 0.5,
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
            overflow: 'hidden', fontStyle: 'italic',
          }}>
            {item.notes}
          </Typography>
        )}
      </Box>

      {/* Move day buttons (alleen voor niet-afgeronde taken) */}
      {!item.isComplete && (
        <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
          {onMoveDay && (
            <Tooltip title={moveDayDirection === 'tomorrow' ? 'Naar morgen' : 'Naar vandaag'}>
              <IconButton
                size="small"
                onClick={() => onMoveDay(item)}
                sx={{ p: 0.25, color: '#adb3b5', '&:hover': { color: '#526075', backgroundColor: '#d5e3fd' } }}
              >
                {moveDayDirection === 'tomorrow'
                  ? <ArrowForwardIcon sx={{ fontSize: 16 }} />
                  : <ArrowBackIcon sx={{ fontSize: 16 }} />
                }
              </IconButton>
            </Tooltip>
          )}
          {onOpenDatePicker && (
            <Tooltip title="Verplaats naar andere dag">
              <IconButton
                size="small"
                onClick={(e) => onOpenDatePicker(item, e.currentTarget)}
                sx={{ p: 0.25, color: '#adb3b5', '&:hover': { color: '#526075', backgroundColor: '#d5e3fd' } }}
              >
                <CalendarIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}

      {/* Opgestuurd knop (alleen voor afgeronde taken, niet al Opgestuurd) */}
      {item.isComplete && onMarkSent && item.priority !== 'Opgestuurd' && item._airtableId && (
        <Tooltip title="Markeer als Opgestuurd">
          <IconButton
            size="small"
            onClick={() => onMarkSent(item)}
            sx={{ p: 0.25, color: '#8b5cf6', flexShrink: 0, '&:hover': { backgroundColor: '#d5e3fd' } }}
          >
            <OpenInNewIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      )}
      {item.priority === 'Opgestuurd' && (
        <Chip label="Opgestuurd" size="small" sx={{ height: 18, fontSize: '0.6rem', bgcolor: '#d5e3fd', color: '#8b5cf6', fontWeight: 600, flexShrink: 0 }} />
      )}

      {/* Source badge */}
      {item.source === 'mstodo' && (
        <Tooltip title="Microsoft To-Do"><TodoIcon sx={{ fontSize: 14, color: '#3b82f6' }} /></Tooltip>
      )}
      {item.source === 'action' && (
        <Tooltip title="Actiepunt"><NoteIcon sx={{ fontSize: 14, color: '#f59e0b' }} /></Tooltip>
      )}
    </Box>
  );
}

// ─── Source Item (in sidebar, draggable into today) ──────────────────────────

function SourceItem({ item, onAdd, onAddToDate }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
    data: { item, isSource: true },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1,
        p: 1, borderRadius: 1.5,
        backgroundColor: '#fff',
        border: '1px solid #f3f4f6',
        cursor: 'grab',
        '&:hover': { borderColor: '#c7d2fe', backgroundColor: '#fafafe' },
      }}
      {...attributes}
      {...listeners}
    >
      <DragIcon sx={{ fontSize: 14, color: '#d1d5db' }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{
          fontSize: '0.8rem', fontWeight: 500, color: '#374151',
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          overflow: 'hidden', lineHeight: 1.4,
        }}>
          {item.title}
        </Typography>
        {(item.projectName || item.startDate || item.deadline) && (
          <Typography variant="caption" sx={{ color: '#adb3b5', fontSize: '0.65rem' }}>
            {item.projectName || ''}{item.projectName && (item.startDate || item.deadline) ? ' · ' : ''}{item.startDate ? formatDate(item.startDate) : item.deadline ? formatDate(item.deadline) : ''}
          </Typography>
        )}
      </Box>
      <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
        <Tooltip title="Vandaag">
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onAdd(item); }}
            onPointerDown={(e) => e.stopPropagation()}
            sx={{ p: 0.25 }}>
            <AddIcon sx={{ fontSize: 16, color: '#526075' }} />
          </IconButton>
        </Tooltip>
        {onAddToDate && (
          <Tooltip title="Andere dag">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onAddToDate(item, e.currentTarget); }}
              onPointerDown={(e) => e.stopPropagation()}
              sx={{ p: 0.25 }}>
              <CalendarIcon sx={{ fontSize: 14, color: '#adb3b5' }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
}

// ─── Drop Zone ───────────────────────────────────────────────────────────────

function DropZone({ id, children }) {
  const { setNodeRef, isOver } = useDroppable({ id });

  return (
    <Box
      ref={setNodeRef}
      sx={{
        minHeight: 120,
        borderRadius: '0.75rem',
        border: isOver ? '2px dashed #526075' : '2px dashed transparent',
        backgroundColor: isOver ? 'rgba(82,96,117,0.04)' : 'transparent',
        transition: 'all 0.2s ease',
        p: isOver ? 1 : 0,
      }}
    >
      {children}
    </Box>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function Vandaag() {
  const { instance } = useMsal();
  const activeAccount = instance.getActiveAccount();
  const userEmail = activeAccount?.username || '';
  const userName = activeAccount?.name || '';

  // State
  const [todayTasks, setTodayTasks] = useState([]);
  const [tomorrowTasks, setTomorrowTasks] = useState([]);
  const [backlogTasks, setBacklogTasks] = useState([]);
  const [msTodos, setMsTodos] = useState([]);
  const [msListId, setMsListId] = useState(null);
  const [actionItems, setActionItems] = useState([]);
  const [loading, setLoading] = useState({ tasks: true, mstodo: true, actions: true });
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCompleted, setShowCompleted] = useState(false);
  const [quickAddText, setQuickAddText] = useState('');
  const [dragActive, setDragActive] = useState(null);
  const [todayDeadlines, setTodayDeadlines] = useState([]);
  const [overdueDeadlines, setOverdueDeadlines] = useState([]);
  const [showInternalDeadlines, setShowInternalDeadlines] = useState(false);
  const [dlAnchor, setDlAnchor] = useState(null);
  const [selectedDl, setSelectedDl] = useState(null);
  const [showTomorrow, setShowTomorrow] = useState(true);
  const [selectedTask, setSelectedTask] = useState(null); // task detail popover
  const [taskAnchor, setTaskAnchor] = useState(null);
  const [editingNotes, setEditingNotes] = useState('');
  const [datePickerAnchor, setDatePickerAnchor] = useState(null); // datepicker popover
  const [datePickerItem, setDatePickerItem] = useState(null);
  const [datePickerValue, setDatePickerValue] = useState('');
  const [userRecordId, setUserRecordId] = useState(null);
  const [backlogQuickAdd, setBacklogQuickAdd] = useState('');
  const [todoQuickAdd, setTodoQuickAdd] = useState('');
  const pendingCreatesRef = useRef(new Map());
  const navigate = useNavigate();

  // Fetch the user's Personen record ID — required as Assignee on new Airtable tasks,
  // otherwise fetchTasks (which filters by Email (Assignee)) won't return them.
  useEffect(() => {
    if (!userEmail) return;
    authFetch(`${BACKEND_URL}/Api/personen?email=${encodeURIComponent(userEmail)}`)
      .then(r => r.json())
      .then(data => {
        const person = (data.personen || []).find(p =>
          p.fields?.Email === userEmail || p.fields?.['Email (from Account)']?.[0] === userEmail
        );
        if (person) setUserRecordId(person.id);
      })
      .catch(() => {});
  }, [userEmail]);

  // PL-todo's: wat jij als projectleider vandaag nog moet regelen (feedback in v2/DEF-taken).
  const [plTodos, setPlTodos] = useState([]);
  const laadPlTodos = useCallback(async () => {
    if (!userEmail) return;
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/pl-todos`);
      if (!r.ok) return;
      const d = await r.json();
      setPlTodos(d.todos || []);
    } catch { /* geen todo's is geen fout in beeld waard */ }
  }, [userEmail]);
  useEffect(() => { laadPlTodos(); }, [laadPlTodos]);

  // Overlegmodule: mijn overleggen deze week + open acties (compact). 403 = geen recht → blok blijft weg.
  const [overleg, setOverleg] = useState(null);
  const laadOverleg = useCallback(async () => {
    if (!userEmail) return;
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/overleg/mijn-week?compact=1`);
      if (!r.ok) return;
      const d = await r.json();
      const iets = (d.sessies || []).length || (d.todos || []).length || (d.in_te_vullen || []).length || (d.niet_gehouden || []).length;
      setOverleg(iets ? d : null);
    } catch { /* stil: geen overleggen is geen fout */ }
  }, [userEmail]);
  useEffect(() => { laadOverleg(); }, [laadOverleg]);

  // DnD sensors
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  // ─── Data Fetching ───────────────────────────────────────────────────────

  const fetchTasks = useCallback(async () => {
    if (!userEmail) return;
    setLoading(prev => ({ ...prev, tasks: true }));
    try {
      const resp = await authFetch(`${BACKEND_URL}/Api/my-tasks?email=${encodeURIComponent(userEmail)}`);
      const data = await leesJson(resp, 'Taken laden');
      const tasks = (data.tasks || []).map(mapAirtableTask);
      const today = todayStr();
      const tomorrow = tomorrowStr();
      setTodayTasks(tasks.filter(t => t.startDate === today && !HIDE_FROM_DAY.has(t.priority)));
      setTomorrowTasks(tasks.filter(t => t.startDate === tomorrow && !t.isComplete && !HIDE_FROM_DAY.has(t.priority)));
      // Een voorbije draaidag of vrije dag is geen backlogwerk (Joris, 3 nov 2026); een die nog komt wel.
      setBacklogTasks(tasks.filter(t => t.startDate !== today && t.startDate !== tomorrow && !t.isComplete && !SKIP_PRIO_BACKLOG.has(t.priority) && !isVoorbijeDagstaat(t, today)));
    } catch (err) {
      console.error('[Vandaag] Failed to fetch tasks:', err);
      toast.error('Taken laden mislukt');
    } finally {
      setLoading(prev => ({ ...prev, tasks: false }));
    }
  }, [userEmail]);

  const fetchMsTodos = useCallback(async () => {
    if (!userEmail) return;
    const account = instance.getActiveAccount();
    if (!account) return;
    setLoading(prev => ({ ...prev, mstodo: true }));
    try {
      initMicrosoftTodoService(instance, account);
      const result = await getMyTodos(false);
      // Deduplicate by task ID (MS Graph can occasionally return duplicates)
      const seen = new Set();
      const uniqueTasks = (result.tasks || []).filter(t => {
        if (seen.has(t.id)) return false;
        seen.add(t.id);
        return true;
      });
      setMsTodos(uniqueTasks.map(t => mapMsTodo(t, result.listId)));
      setMsListId(result.listId);
    } catch (err) {
      console.error('[Vandaag] MS To-Do fetch failed:', err);
      // Not critical - just show empty
    } finally {
      setLoading(prev => ({ ...prev, mstodo: false }));
    }
  }, [userEmail, instance]);

  const fetchActionItems = useCallback(async () => {
    if (!userEmail) return;
    setLoading(prev => ({ ...prev, actions: true }));
    try {
      const resp = await authFetch(`${BACKEND_URL}/Api/my-action-items?email=${encodeURIComponent(userEmail)}`);
      const data = await leesJson(resp, 'Actiepunten laden');
      setActionItems((data.actionItems || []).map(mapActionItem));
    } catch (err) {
      console.error('[Vandaag] Action items fetch failed:', err);
      meldFout(err, 'Actiepunten laden');
    } finally {
      setLoading(prev => ({ ...prev, actions: false }));
    }
  }, [userEmail]);

  const fetchDeadlines = useCallback(async () => {
    if (!userEmail) return;
    try {
      const today = todayStr();
      // Fetch today + overdue (endDate=today, no startDate → includes all past items)
      const resp = await authFetch(`${BACKEND_URL}/Api/my-deadlines?email=${encodeURIComponent(userEmail)}&endDate=${today}`);
      const data = await leesJson(resp, 'Deadlines laden');
      const all = data.byDate || [];
      const doneStatuses = AFGEROND_SET;
      const todayItems = all.filter(d => d.deadline?.slice(0, 10) === today);
      const overdueItems = all.filter(d => {
        const dt = d.deadline?.slice(0, 10);
        return dt && dt < today && !doneStatuses.has(d.status);
      });
      setTodayDeadlines(todayItems);
      setOverdueDeadlines(overdueItems);
    } catch (err) {
      console.error('[Vandaag] Deadlines fetch failed:', err);
      meldFout(err, 'Deadlines laden');
    }
  }, [userEmail]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);
  useEffect(() => registreerOngedaanSneltoets(), []);
  useEffect(() => { fetchMsTodos(); }, [fetchMsTodos]);
  useEffect(() => { fetchActionItems(); }, [fetchActionItems]);
  useEffect(() => { fetchDeadlines(); }, [fetchDeadlines]);

  const refreshAll = () => { fetchTasks(); fetchMsTodos(); fetchActionItems(); fetchDeadlines(); };

  // ─── Task Actions ────────────────────────────────────────────────────────

  // Resolve the Airtable record id for an item, awaiting a pending create if needed.
  // Why: quick-add inserts a temp task with _airtableId: null and POSTs in the background;
  // if the user moves/edits the task before the POST resolves, we'd otherwise lose the change.
  const resolveAirtableId = useCallback(async (item) => {
    if (item._airtableId) return item._airtableId;
    const pending = pendingCreatesRef.current.get(item.id);
    if (!pending) return null;
    try {
      const created = await pending;
      return created?.id || null;
    } catch {
      return null;
    }
  }, []);

  // Ongedaan maken van een verplaatsing: vorige datums terugzetten en de lijsten verversen.
  const herstelDatums = useCallback(async (airtableId, vorige) => {
    await authFetch(`${BACKEND_URL}/Api/tasks/${airtableId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(vorige),
    });
    await fetchTasks();
  }, [fetchTasks]);

  // Move a backlog task to today
  const moveToToday = useCallback(async (item) => {
    const today = todayStr();
    if (item.source === 'airtable' && item._airtableId) {
      const vorige = vorigeDatums(item);
      // Optimistic update
      setBacklogTasks(prev => prev.filter(t => t.id !== item.id));
      setTodayTasks(prev => [...prev, { ...item, startDate: today }]);
      try {
        await authFetch(`${BACKEND_URL}/Api/tasks/${item._airtableId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ StartDateUitvoerder: today, EndDateUitvoerder: today }),
        });
        biedOngedaanAan('Naar vandaag verplaatst', () => herstelDatums(item._airtableId, vorige));
      } catch (err) {
        // Revert
        setTodayTasks(prev => prev.filter(t => t.id !== item.id));
        setBacklogTasks(prev => [...prev, item]);
        toast.error('Verplaatsen mislukt');
      }
    } else if (item.source === 'mstodo') {
      // Create Airtable task from MS To-Do
      setMsTodos(prev => prev.filter(t => t.id !== item.id));
      const tempId = `temp-${Date.now()}`;
      const tempTask = { ...item, id: tempId, source: 'airtable', startDate: today };
      setTodayTasks(prev => [...prev, tempTask]);
      try {
        const body = {
          NaamTaak: item.title,
          StartDateUitvoerder: today,
          EndDateUitvoerder: today,
          PrioNr: 'Geen prio',
        };
        if (userRecordId) body.Assignee = [userRecordId];
        const resp = await authFetch(`${BACKEND_URL}/Api/tasks`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const created = await leesJson(resp, 'Taak aanmaken');
        // Replace temp with real task
        setTodayTasks(prev => prev.map(t => t.id === tempId ? mapAirtableTask(created.task || created) : t));
        toast.success('Taak aangemaakt vanuit To-Do');
      } catch (err) {
        setTodayTasks(prev => prev.filter(t => t.id !== tempId));
        setMsTodos(prev => [...prev, item]);
        toast.error('Aanmaken mislukt');
      }
    } else if (item.source === 'action') {
      // Create Airtable task from action item
      setActionItems(prev => prev.filter(t => t.id !== item.id));
      const tempId = `temp-${Date.now()}`;
      const tempTask = { ...item, id: tempId, source: 'airtable', startDate: today };
      setTodayTasks(prev => [...prev, tempTask]);
      try {
        const body = {
          NaamTaak: item.title,
          StartDateUitvoerder: today,
          EndDateUitvoerder: today,
          PrioNr: 'Geen prio',
        };
        if (item.projectId) body.Project = [item.projectId];
        if (userRecordId) body.Assignee = [userRecordId];
        const resp = await authFetch(`${BACKEND_URL}/Api/tasks`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const created = await leesJson(resp, 'Taak aanmaken');
        setTodayTasks(prev => prev.map(t => t.id === tempId ? mapAirtableTask(created.task || created) : t));
        toast.success('Taak aangemaakt vanuit actiepunt');
      } catch (err) {
        setTodayTasks(prev => prev.filter(t => t.id !== tempId));
        setActionItems(prev => [...prev, item]);
        toast.error('Aanmaken mislukt');
      }
    }
  }, [userEmail, userRecordId, herstelDatums]);

  // Move a task to tomorrow
  const moveToTomorrow = useCallback(async (item) => {
    const tomorrow = tomorrowStr();
    const vorige = vorigeDatums(item);
    // Optimistic update
    setTodayTasks(prev => prev.filter(t => t.id !== item.id));
    setTomorrowTasks(prev => [...prev, { ...item, startDate: tomorrow }]);
    const airtableId = await resolveAirtableId(item);
    if (!airtableId) {
      setTomorrowTasks(prev => prev.filter(t => t.id !== item.id));
      setTodayTasks(prev => [...prev, item]);
      toast.error('Verplaatsen mislukt');
      return;
    }
    try {
      await authFetch(`${BACKEND_URL}/Api/tasks/${airtableId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ StartDateUitvoerder: tomorrow, EndDateUitvoerder: tomorrow }),
      });
      biedOngedaanAan('Naar morgen verplaatst', () => herstelDatums(airtableId, vorige));
    } catch (err) {
      setTomorrowTasks(prev => prev.filter(t => t.id !== item.id));
      setTodayTasks(prev => [...prev, item]);
      toast.error('Verplaatsen mislukt');
    }
  }, [resolveAirtableId, herstelDatums]);

  // Move a task from tomorrow back to today
  const moveFromTomorrowToToday = useCallback(async (item) => {
    const today = todayStr();
    const vorige = vorigeDatums(item);
    setTomorrowTasks(prev => prev.filter(t => t.id !== item.id));
    setTodayTasks(prev => [...prev, { ...item, startDate: today }]);
    const airtableId = await resolveAirtableId(item);
    if (!airtableId) {
      setTodayTasks(prev => prev.filter(t => t.id !== item.id));
      setTomorrowTasks(prev => [...prev, item]);
      toast.error('Verplaatsen mislukt');
      return;
    }
    try {
      await authFetch(`${BACKEND_URL}/Api/tasks/${airtableId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ StartDateUitvoerder: today, EndDateUitvoerder: today }),
      });
      biedOngedaanAan('Naar vandaag verplaatst', () => herstelDatums(airtableId, vorige));
    } catch (err) {
      setTodayTasks(prev => prev.filter(t => t.id !== item.id));
      setTomorrowTasks(prev => [...prev, item]);
      toast.error('Verplaatsen mislukt');
    }
  }, [resolveAirtableId, herstelDatums]);

  // Move a task (or source item) to a specific date
  const moveToDate = useCallback(async (item, dateStr) => {
    if (!dateStr) return;
    const today = todayStr();
    const tomorrow = tomorrowStr();

    // Existing airtable task or freshly-created (still pending) task
    if (item._airtableId || pendingCreatesRef.current.has(item.id)) {
      const vorige = vorigeDatums(item);
      // Remove from current list
      setTodayTasks(prev => prev.filter(t => t.id !== item.id));
      setTomorrowTasks(prev => prev.filter(t => t.id !== item.id));
      setBacklogTasks(prev => prev.filter(t => t.id !== item.id));
      // Add to target list
      const updated = { ...item, startDate: dateStr };
      if (dateStr === today) setTodayTasks(prev => [...prev, updated]);
      else if (dateStr === tomorrow) setTomorrowTasks(prev => [...prev, updated]);
      // else it goes to backlog on next refresh
      const airtableId = await resolveAirtableId(item);
      if (!airtableId) {
        toast.error('Verplaatsen mislukt');
        fetchTasks();
        return;
      }
      try {
        await authFetch(`${BACKEND_URL}/Api/tasks/${airtableId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ StartDateUitvoerder: dateStr, EndDateUitvoerder: dateStr }),
        });
        biedOngedaanAan(`Verplaatst naar ${formatDate(dateStr)}`, () => herstelDatums(airtableId, vorige));
      } catch (err) {
        toast.error('Verplaatsen mislukt');
        fetchTasks(); // refresh to restore state
      }
    } else if (item.source === 'mstodo' || item.source === 'action') {
      // Create new airtable task for this date
      setMsTodos(prev => prev.filter(t => t.id !== item.id));
      setActionItems(prev => prev.filter(t => t.id !== item.id));
      const tempId = `temp-${Date.now()}`;
      const tempTask = { ...item, id: tempId, source: 'airtable', startDate: dateStr };
      if (dateStr === today) setTodayTasks(prev => [...prev, tempTask]);
      else if (dateStr === tomorrow) setTomorrowTasks(prev => [...prev, tempTask]);
      try {
        const body = { NaamTaak: item.title, StartDateUitvoerder: dateStr, EndDateUitvoerder: dateStr, PrioNr: 'Geen prio' };
        if (item.projectId) body.Project = [item.projectId];
        if (userRecordId) body.Assignee = [userRecordId];
        const resp = await authFetch(`${BACKEND_URL}/Api/tasks`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const created = await leesJson(resp, 'Taak aanmaken');
        const mapped = mapAirtableTask(created.task || created);
        setTodayTasks(prev => prev.map(t => t.id === tempId ? mapped : t));
        setTomorrowTasks(prev => prev.map(t => t.id === tempId ? mapped : t));
        toast.success(`Taak aangemaakt voor ${formatDate(dateStr)}`);
      } catch (err) {
        setTodayTasks(prev => prev.filter(t => t.id !== tempId));
        setTomorrowTasks(prev => prev.filter(t => t.id !== tempId));
        toast.error('Aanmaken mislukt');
      }
    }
  }, [fetchTasks, resolveAirtableId, userRecordId, herstelDatums]);

  // Update task field in Airtable
  const updateTaskField = useCallback(async (item, fields) => {
    if (!item._airtableId) return;
    try {
      await authFetch(`${BACKEND_URL}/Api/tasks/${item._airtableId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(fields),
      });
      // Update local state
      const updater = (prev) => prev.map(t => {
        if (t.id !== item.id) return t;
        const updated = { ...t };
        if (fields.Opmerking !== undefined) updated.notes = fields.Opmerking;
        if (fields.UrenWerk !== undefined) updated.hours = parseFloat(fields.UrenWerk) || null;
        if (fields.PrioNr !== undefined) updated.priority = fields.PrioNr;
        return updated;
      });
      setTodayTasks(updater);
      setTomorrowTasks(updater);
      toast.success('Bijgewerkt');
    } catch (err) {
      toast.error('Bijwerken mislukt');
    }
  }, []);

  // Mark a completed task as "Opgestuurd"
  const markAsSent = useCallback(async (item) => {
    if (!item._airtableId) return;
    // Optimistic update
    setTodayTasks(prev => prev.map(t =>
      t.id === item.id ? { ...t, priority: 'Opgestuurd' } : t
    ));
    setTomorrowTasks(prev => prev.map(t =>
      t.id === item.id ? { ...t, priority: 'Opgestuurd' } : t
    ));
    try {
      await authFetch(`${BACKEND_URL}/Api/tasks/${item._airtableId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ PrioNr: 'Opgestuurd', Afgerond: true }),
      });
      // Terug naar de vorige status (meestal "Afgerond") — misklik op het kleine knopje.
      const terugPrio = item.priority || 'Afgerond';
      const zetTerug = (prev) => prev.map(t => (t.id === item.id ? { ...t, priority: terugPrio } : t));
      biedOngedaanAan('Gemarkeerd als Opgestuurd', async () => {
        await authFetch(`${BACKEND_URL}/Api/tasks/${item._airtableId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ PrioNr: terugPrio, Afgerond: !!item.isComplete }),
        });
        setTodayTasks(zetTerug);
        setTomorrowTasks(zetTerug);
      });
    } catch (err) {
      // Revert
      setTodayTasks(prev => prev.map(t =>
        t.id === item.id ? { ...t, priority: item.priority } : t
      ));
      setTomorrowTasks(prev => prev.map(t =>
        t.id === item.id ? { ...t, priority: item.priority } : t
      ));
      toast.error('Status wijzigen mislukt');
    }
  }, []);

  // Toggle task completion
  const toggleComplete = useCallback(async (item) => {
    const newComplete = !item.isComplete;
    // Optimistic
    setTodayTasks(prev => prev.map(t =>
      t.id === item.id ? { ...t, isComplete: newComplete, priority: newComplete ? 'Afgerond' : 'Geen prio' } : t
    ));
    try {
      if (item._airtableId) {
        await authFetch(`${BACKEND_URL}/Api/tasks/${item._airtableId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            PrioNr: newComplete ? 'Afgerond' : 'Geen prio',
            Afgerond: newComplete,
          }),
        });
      }
      // Sync back to MS To-Do if applicable
      if (newComplete && item._msTodoId && item._msListId) {
        try { await completeMsTodo(item._msListId, item._msTodoId); } catch { /* silent */ }
      }
      if (newComplete) {
        const terugPrio = item.priority && !AFGEROND_SET.has(item.priority) ? item.priority : 'Geen prio';
        biedOngedaanAan(`"${item.title}" afgerond`, async () => {
          if (item._airtableId) {
            await authFetch(`${BACKEND_URL}/Api/tasks/${item._airtableId}`, {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ PrioNr: terugPrio, Afgerond: false }),
            });
          }
          if (item._msTodoId && item._msListId) {
            try { await reopenMsTodo(item._msListId, item._msTodoId); } catch { /* To Do heropenen is best effort */ }
          }
          setTodayTasks(prev => prev.map(t =>
            t.id === item.id ? { ...t, isComplete: false, priority: terugPrio } : t
          ));
        });
      }
    } catch (err) {
      // Revert
      setTodayTasks(prev => prev.map(t =>
        t.id === item.id ? { ...t, isComplete: !newComplete, priority: item.priority } : t
      ));
      toast.error('Status bijwerken mislukt');
    }
  }, []);

  // Quick add task
  const quickAdd = useCallback(async () => {
    const text = quickAddText.trim();
    if (!text) return;
    if (!userRecordId) {
      toast.error('Profiel nog niet geladen — probeer opnieuw');
      return;
    }
    setQuickAddText('');
    const today = todayStr();
    const tempId = `temp-${Date.now()}`;
    setTodayTasks(prev => [...prev, {
      id: tempId, source: 'airtable', title: text, isComplete: false,
      priority: 'Geen prio', type: null, hours: null, deadline: null,
      projectName: null, projectId: null, startDate: today, _airtableId: null,
    }]);
    const createPromise = (async () => {
      const resp = await authFetch(`${BACKEND_URL}/Api/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          NaamTaak: text,
          StartDateUitvoerder: today,
          EndDateUitvoerder: today,
          PrioNr: 'Geen prio',
          Assignee: [userRecordId],
        }),
      });
      if (!resp.ok) throw new Error('create failed');
      const data = await resp.json();
      return data.task || data;
    })();
    pendingCreatesRef.current.set(tempId, createPromise);
    try {
      const created = await createPromise;
      setTodayTasks(prev => prev.map(t => t.id === tempId ? mapAirtableTask(created) : t));
    } catch (err) {
      setTodayTasks(prev => prev.filter(t => t.id !== tempId));
      toast.error('Taak aanmaken mislukt');
    } finally {
      pendingCreatesRef.current.delete(tempId);
    }
  }, [quickAddText, userRecordId]);

  // Quick add directly to backlog (no start date — lands in backlog after refresh and immediately)
  const quickAddBacklog = useCallback(async () => {
    const text = backlogQuickAdd.trim();
    if (!text) return;
    if (!userRecordId) {
      toast.error('Profiel nog niet geladen — probeer opnieuw');
      return;
    }
    setBacklogQuickAdd('');
    const tempId = `temp-bl-${Date.now()}`;
    setBacklogTasks(prev => [...prev, {
      id: tempId, source: 'airtable', title: text, isComplete: false,
      priority: 'Geen prio', type: null, hours: null, deadline: null,
      projectName: null, projectId: null, startDate: null, _airtableId: null,
    }]);
    try {
      const resp = await authFetch(`${BACKEND_URL}/Api/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          NaamTaak: text,
          PrioNr: 'Geen prio',
          Assignee: [userRecordId],
        }),
      });
      if (!resp.ok) throw new Error('create failed');
      const data = await resp.json();
      const created = data.task || data;
      setBacklogTasks(prev => prev.map(t => t.id === tempId ? mapAirtableTask(created) : t));
      toast.success('Taak toegevoegd aan backlog');
    } catch (err) {
      setBacklogTasks(prev => prev.filter(t => t.id !== tempId));
      toast.error('Taak aanmaken mislukt');
    }
  }, [backlogQuickAdd, userRecordId]);

  // Quick add Microsoft To-Do task
  const quickAddTodo = useCallback(async () => {
    const text = todoQuickAdd.trim();
    if (!text) return;
    if (!msListId) {
      toast.error('Microsoft To-Do lijst niet beschikbaar');
      return;
    }
    setTodoQuickAdd('');
    try {
      const created = await createMsTodo(msListId, { title: text });
      setMsTodos(prev => [...prev, mapMsTodo(created, msListId)]);
      toast.success('To-Do toegevoegd');
    } catch (err) {
      console.error('[Vandaag] MS To-Do create failed:', err);
      toast.error('To-Do aanmaken mislukt');
    }
  }, [todoQuickAdd, msListId]);

  // ─── DnD Handlers ────────────────────────────────────────────────────────

  const handleDragStart = (event) => {
    const item = event.active.data?.current?.item;
    setDragActive(item || null);
  };

  const handleDragEnd = (event) => {
    setDragActive(null);
    const { active, over } = event;
    if (!over) return;

    const item = active.data?.current?.item;
    const isSource = active.data?.current?.isSource;

    // Handle drops into today or tomorrow zone
    if (isSource && item) {
      if (over.id === 'today-zone') moveToToday(item);
      else if (over.id === 'tomorrow-zone') {
        moveToDate(item, tomorrowStr());
      }
    }
  };

  // ─── Computed Data ───────────────────────────────────────────────────────

  const activeTodayTasks = todayTasks.filter(t => !t.isComplete);
  const completedTodayTasks = todayTasks.filter(t => t.isComplete);
  const totalHours = activeTodayTasks.reduce((sum, t) => sum + (t.hours || 0), 0);
  const totalHoursTomorrow = tomorrowTasks.reduce((sum, t) => sum + (t.hours || 0), 0);

  // Outlook-agenda ernaast leggen: afspraken zonder Airtable-taak tonen we als
  // alleen-lezen agenda-item — de dag is dan altijd compleet, ook als de
  // Outlook→Airtable-sync iets oversloeg (bv. sollicitaties).
  const [calEvents, setCalEvents] = useState({ today: [], tomorrow: [] });
  useEffect(() => {
    (async () => {
      try {
        const [t, m] = await Promise.all([fetchDayEvents(todayStr()), fetchDayEvents(tomorrowStr())]);
        setCalEvents({ today: t, tomorrow: m });
      } catch (e) { console.warn('[Vandaag] agenda ophalen mislukt:', e.message); }
    })();
  }, []);

  const calendarOnly = useCallback((events, tasks) => {
    const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    return events
      .filter((ev) => !tasks.some((t) => {
        // Dedupe alleen op (vrijwel) gelijke titel; tijd-gelijkheid alléén is te
        // grof — twee verschillende afspraken om 10:00 filterden elkaar weg.
        const a = norm(t.title); const b = norm(ev.subject);
        if (!a || !b) return false;
        if (a === b) return true;
        const evHM = ev.start.slice(11, 16);
        const taskHM = t.startTime
          ? new Date(t.startTime).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })
          : null;
        return taskHM === evHM && (a.includes(b) || b.includes(a));
      }))
      .map((ev) => ({
        id: `cal-${ev.id}`, title: ev.subject, start: ev.start, end: ev.end,
        startTime: ev.start, isCalendarOnly: true, priority: 'Afspraak',
      }));
  }, []);

  // Afspraken met een begintijd chronologisch bovenaan; de rest op prioriteit.
  const byTimeThenPriority = useCallback((a, b) => {
    if (a.startTime && b.startTime) return new Date(a.startTime) - new Date(b.startTime);
    if (a.startTime) return -1;
    if (b.startTime) return 1;
    const ia = PRIO_ORDER.indexOf(a.priority);
    const ib = PRIO_ORDER.indexOf(b.priority);
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
  }, []);

  const sortedTomorrow = useMemo(() =>
    [...tomorrowTasks, ...calendarOnly(calEvents.tomorrow, tomorrowTasks)].sort(byTimeThenPriority),
  [tomorrowTasks, calEvents.tomorrow, calendarOnly, byTimeThenPriority]);

  // Open task detail popover
  const handleClickTask = useCallback((item, event) => {
    setSelectedTask(item);
    setTaskAnchor(event?.currentTarget || null);
    setEditingNotes(item.notes || '');
  }, []);

  // Open datepicker for an item
  const openDatePicker = useCallback((item, anchorEl) => {
    setDatePickerItem(item);
    setDatePickerAnchor(anchorEl);
    setDatePickerValue(item.startDate || tomorrowStr());
  }, []);

  const handleDatePickerConfirm = useCallback(() => {
    if (datePickerItem && datePickerValue) {
      moveToDate(datePickerItem, datePickerValue);
    }
    setDatePickerAnchor(null);
    setDatePickerItem(null);
  }, [datePickerItem, datePickerValue, moveToDate]);

  // Vandaag: afspraken op tijdsvolgorde bovenaan, daarna taken op prioriteit.
  // Agenda-items zonder taak schuiven er chronologisch tussen.
  const sortedActive = useMemo(() =>
    [...activeTodayTasks, ...calendarOnly(calEvents.today, todayTasks)].sort(byTimeThenPriority),
  [activeTodayTasks, calEvents.today, todayTasks, calendarOnly, byTimeThenPriority]);

  // Filtered source items — also filter out items named "Reistijd ..." (travel time, not actionable)
  const q = searchQuery.toLowerCase();
  const filteredBacklog = useMemo(() =>
    backlogTasks
      .filter(t => !t.title.toLowerCase().startsWith('reistijd'))
      .filter(t => !q || t.title.toLowerCase().includes(q) || t.projectName?.toLowerCase().includes(q)),
  [backlogTasks, q]);
  const filteredMsTodos = useMemo(() =>
    msTodos.filter(t => !q || t.title.toLowerCase().includes(q)),
  [msTodos, q]);
  const filteredActions = useMemo(() =>
    actionItems.filter(t => !q || t.title.toLowerCase().includes(q) || t.projectName?.toLowerCase().includes(q)),
  [actionItems, q]);

  // Group backlog: overdue → this week → next week → later → no date
  const groupedBacklog = useMemo(() => {
    const overdue = [];
    const thisWeek = [];
    const nextWeek = [];
    const noDate = [];
    const rest = [];

    // Calculate days until end of this week (Sunday)
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const dayOfWeek = now.getDay(); // 0=Sun, 1=Mon, ...
    const daysToSunday = dayOfWeek === 0 ? 0 : 7 - dayOfWeek;
    const daysToNextSunday = daysToSunday + 7;

    filteredBacklog.forEach(t => {
      if (!t.startDate && !t.deadline) noDate.push(t);
      else {
        const dl = daysUntil(t.startDate || t.deadline);
        if (dl < 0) overdue.push(t);
        else if (dl <= daysToSunday) thisWeek.push(t);
        else if (dl <= daysToNextSunday) nextWeek.push(t);
        else rest.push(t);
      }
    });
    return { overdue, thisWeek, nextWeek, noDate, rest };
  }, [filteredBacklog]);

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <Box sx={{ width: '100%', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 10 }}>
        <PageHeader
          icon="today"
          title="Vandaag"
          subtitle={`${longDate()} — ${userName.split(' ')[0]}`}
          rightActions={
            <>
              <Chip
                label={`${activeTodayTasks.length} taken`}
                size="small"
                sx={{ fontFamily: '"Inter", system-ui, sans-serif', fontWeight: 700, fontSize: '0.72rem', height: 24, backgroundColor: '#d5e3fd', color: '#526075' }}
              />
              {totalHours > 0 && (
                <Chip
                  icon={<ClockIcon sx={{ fontSize: 14 }} />}
                  label={`${totalHours}u gepland`}
                  size="small"
                  sx={{ fontFamily: '"Inter", system-ui, sans-serif', fontWeight: 700, fontSize: '0.72rem', height: 24, backgroundColor: '#ecfdf5', color: '#10b981' }}
                />
              )}
              <Tooltip title="Vernieuwen">
                <IconButton onClick={refreshAll} size="small" sx={{ color: '#adb3b5', '&:hover': { color: '#526075' } }}>
                  <RefreshIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            </>
          }
        />

        {/* Main Layout */}
        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>

          {/* ═══ LEFT: Today's Tasks ═══ */}
          <Box sx={{ flex: '1 1 40%', minWidth: 0 }}>
            {/* Overleg-voorbereiding: open invulblok als taakregel (alleen voor deelnemers, weg na indienen) */}
            <OverlegTaak />

            {/* Milestones & Deadlines Banner */}
            {(todayDeadlines.length > 0 || overdueDeadlines.length > 0) && (() => {
              // Filter: hide internal Full Frame projects unless toggled
              const isInternal = (projectName) => {
                if (!projectName) return false;
                const afterNum = projectName.replace(/^\d+\.\s*/, '');
                return afterNum.startsWith('Full Frame');
              };
              const filterItems = (items) => items.filter(d => {
                if (!showInternalDeadlines && isInternal(d.projectName)) return false;
                if (d.type === 'task' && !d.deliverableId) return false;
                return true;
              });
              const filtered = filterItems(todayDeadlines);
              const filteredOverdue = filterItems(overdueDeadlines);
              const internalCount = [...todayDeadlines, ...overdueDeadlines].filter(d => isInternal(d.projectName)).length;
              const taskItems = filtered.filter(d => d.type === 'task');
              const deliverableItems = filtered.filter(d => d.type === 'deliverable');
              const overdueTaskItems = filteredOverdue.filter(d => d.type === 'task');
              const overdueDeliverableItems = filteredOverdue.filter(d => d.type === 'deliverable');
              const allOverdue = [...overdueTaskItems, ...overdueDeliverableItems];
              if (filtered.length === 0 && allOverdue.length === 0 && internalCount === 0) return null;
              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
                  {/* Overdue items */}
                  {allOverdue.length > 0 && (
                    <Paper elevation={0} sx={{ p: 2, borderRadius: '0.75rem', border: '1px solid #fca5a5', backgroundColor: '#fef2f2' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <FlagIcon sx={{ fontSize: 18, color: '#dc2626' }} />
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#991b1b' }}>
                          Nog te doen
                        </Typography>
                        <Chip label={allOverdue.length} size="small" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, backgroundColor: '#fca5a5', color: '#991b1b' }} />
                      </Box>
                      {allOverdue.map(d => (
                        <Box key={d.id} onClick={(e) => { setDlAnchor(e.currentTarget); setSelectedDl(d); }} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, cursor: 'pointer', '&:hover': { opacity: 0.85 } }}>
                          <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#dc2626', flexShrink: 0 }} />
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem', color: '#2d3335' }}>
                            {d.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#767c7e' }}>
                            — {d.projectName}
                          </Typography>
                          <Chip
                            label={new Date(d.deadline).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })}
                            size="small"
                            sx={{ height: 18, fontSize: '0.6rem', ml: 'auto', backgroundColor: '#fee2e2', color: '#991b1b' }}
                          />
                        </Box>
                      ))}
                    </Paper>
                  )}
                  {/* Task end dates (tussenproducten) */}
                  {taskItems.length > 0 && (
                    <Paper elevation={0} sx={{ p: 2, borderRadius: '0.75rem', border: '1px solid #bae6fd', backgroundColor: '#f0f9ff' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <TaskIcon sx={{ fontSize: 18, color: '#0284c7' }} />
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#0c4a6e' }}>
                          Opleveringen vandaag
                        </Typography>
                        <Chip label={taskItems.length} size="small" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, backgroundColor: '#bae6fd', color: '#0c4a6e' }} />
                      </Box>
                      {taskItems.map(d => (
                        <Box key={d.id} onClick={(e) => { setDlAnchor(e.currentTarget); setSelectedDl(d); }} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, cursor: 'pointer', '&:hover': { opacity: 0.85 } }}>
                          <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#0284c7', flexShrink: 0 }} />
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem', color: '#2d3335' }}>
                            {d.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#767c7e' }}>
                            — {d.projectName}
                          </Typography>
                        </Box>
                      ))}
                    </Paper>
                  )}
                  {/* Deliverable deadlines (finale deadlines) */}
                  {deliverableItems.length > 0 && (
                    <Paper elevation={0} sx={{ p: 2, borderRadius: '0.75rem', border: '1px solid #fde68a', backgroundColor: '#fffbeb' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <FlagIcon sx={{ fontSize: 18, color: '#f59e0b' }} />
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#92400e' }}>
                          Deliverable deadlines vandaag
                        </Typography>
                        <Chip label={deliverableItems.length} size="small" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, backgroundColor: '#fde68a', color: '#92400e' }} />
                      </Box>
                      {deliverableItems.map(d => (
                        <Box key={d.id} onClick={(e) => { setDlAnchor(e.currentTarget); setSelectedDl(d); }} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, cursor: 'pointer', '&:hover': { opacity: 0.85 } }}>
                          <Chip
                            label={d.deadlineType || 'Intern'}
                            size="small"
                            sx={{
                              height: 20, fontSize: '0.65rem', fontWeight: 600,
                              backgroundColor: d.deadlineType === 'Klant' ? '#fef3c7' : d.deadlineType === 'Hard' ? '#fee2e2' : '#e0e7ff',
                              color: d.deadlineType === 'Klant' ? '#92400e' : d.deadlineType === 'Hard' ? '#991b1b' : '#3730a3',
                            }}
                          />
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem', color: '#2d3335' }}>
                            {d.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#767c7e' }}>
                            — {d.projectName}
                          </Typography>
                        </Box>
                      ))}
                    </Paper>
                  )}
                  {/* Toggle for internal projects */}
                  {internalCount > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Checkbox
                        checked={showInternalDeadlines}
                        onChange={(e) => setShowInternalDeadlines(e.target.checked)}
                        size="small"
                        sx={{ p: 0.25 }}
                      />
                      <Typography variant="caption" sx={{ color: '#adb3b5', fontSize: '0.75rem' }}>
                        Toon interne projecten ({internalCount})
                      </Typography>
                    </Box>
                  )}
                </Box>
              );
            })()}
            {/* Deadline popover */}
            <Popover
              open={Boolean(dlAnchor)}
              anchorEl={dlAnchor}
              onClose={() => { setDlAnchor(null); setSelectedDl(null); }}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              slotProps={{ paper: { sx: { p: 2, maxWidth: 340, borderRadius: 2 } } }}
            >
              {selectedDl && (
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', mb: 0.5 }}>
                    {selectedDl.type === 'deliverable' ? '🎯 ' : '📦 '}{selectedDl.name}
                  </Typography>
                  <Typography sx={{ fontSize: '0.8rem', color: '#767c7e', mb: 0.5 }}>
                    {selectedDl.projectNumber ? `${selectedDl.projectNumber}. ` : ''}{selectedDl.projectName?.replace(/^\d+\.\s*/, '')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <Chip label={new Date(selectedDl.deadline).toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' })} size="small" sx={{ fontSize: '0.7rem' }} />
                    {selectedDl.deadlineType && <Chip label={selectedDl.deadlineType} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />}
                  </Box>
                  {selectedDl.type === 'task' && (
                    <Box sx={{ mb: 1.5 }}>
                      <Typography sx={{ fontSize: '0.7rem', color: '#adb3b5', mb: 0.5 }}>Status</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {['Prio 1', 'Prio 2', 'Prio 3', 'Geen prio', 'Wacht op feedback', 'Afgerond', 'Opgestuurd'].map(s => (
                          <Chip
                            key={s}
                            label={s}
                            size="small"
                            variant={selectedDl.status === s ? 'filled' : 'outlined'}
                            sx={{
                              fontSize: '0.65rem', height: 22, cursor: 'pointer',
                              ...(selectedDl.status === s ? { bgcolor: PRIO_COLORS[s] || '#526075', color: '#fff', borderColor: PRIO_COLORS[s] } : { borderColor: '#e5e7eb' }),
                            }}
                            onClick={async () => {
                              try {
                                await authFetch(`${BACKEND_URL}/Api/tasks/${selectedDl.id}`, {
                                  method: 'PATCH',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ fields: { PrioNr: s } }),
                                });
                                setSelectedDl({ ...selectedDl, status: s });
                                toast.success(`Status → ${s}`);
                              } catch { toast.error('Status wijzigen mislukt'); }
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                  <Button
                    size="small"
                    variant="contained"
                    fullWidth
                    onClick={() => { navigate(`/projects/${selectedDl.projectId}`); setDlAnchor(null); }}
                    sx={{ textTransform: 'none', fontSize: '0.8rem' }}
                  >
                    Ga naar project →
                  </Button>
                </Box>
              )}
            </Popover>
            {/* Overleggen — alleen zichtbaar als er deze week iets is */}
            {overleg && (
              <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: '0.75rem', border: '1px solid #c7d2fe', backgroundColor: '#eef2ff' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#3730a3', flex: 1 }}>Overleg deze week</Typography>
                  {overleg.te_laat > 0 && <Chip label={`${overleg.te_laat} actie(s) te laat`} size="small" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, backgroundColor: '#fee2e2', color: '#991b1b' }} />}
                  <Button size="small" onClick={() => navigate('/overleg')} sx={{ textTransform: 'none', fontWeight: 700, minWidth: 0 }}>Mijn week →</Button>
                </Box>
                <NietGehouden items={overleg.niet_gehouden} onVeranderd={laadOverleg} compact />
                {(overleg.sessies || []).map((s) => (
                  <Box key={s.id} onClick={() => navigate(`/overleg/sessie/${s.id}`)} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, cursor: 'pointer', '&:hover': { opacity: 0.85 } }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: s.status === 'bezig' ? '#16a34a' : '#4338ca', flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem', color: '#2d3335', flex: 1 }}>{s.type_naam}</Typography>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                      {new Date(s.gepland_start_nl).toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' })} {new Date(s.gepland_start_nl).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}
                      {s.prep_nodig ? (s.prep_ingediend ? ' · ✓ ingevuld' : ' · invulblok open') : ''}
                    </Typography>
                  </Box>
                ))}
                {(overleg.todos || []).length > 0 && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#3730a3' }}>
                    {(overleg.todos || []).length} open actie(s) uit overleggen{(overleg.in_te_vullen || []).length ? ` · ${overleg.in_te_vullen.length} cijfer(s) in te vullen` : ''}
                  </Typography>
                )}
              </Paper>
            )}
            {/* PL-todo's — alleen zichtbaar als er iets te doen is */}
            {plTodos.length > 0 && (
              <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: '0.75rem', border: '1px solid #fcd34d', backgroundColor: '#fffbeb' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <FlagIcon sx={{ fontSize: 18, color: '#d97706' }} />
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#92400e' }}>
                    Jouw PL-todo's
                  </Typography>
                  <Chip label={plTodos.length} size="small" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, backgroundColor: '#fde68a', color: '#78350f' }} />
                </Box>
                {plTodos.map((t) => (
                  <Box key={t.id} onClick={() => navigate(t.link || '/dagstart')}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, cursor: 'pointer', '&:hover': { opacity: 0.85 } }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: t.urgentie >= 2 ? '#dc2626' : '#d97706', flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem', color: '#2d3335' }}>
                      {t.titel}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#767c7e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      — {t.taskName}{t.projectNumber ? ` · ${t.projectNumber}` : ''}{t.assignee ? ` · ${t.assignee}` : ''}
                    </Typography>
                    {t.wanneer && (
                      <Chip label={t.wanneer} size="small"
                        sx={{ height: 18, fontSize: '0.6rem', ml: 'auto', flexShrink: 0, backgroundColor: t.urgentie >= 2 ? '#fee2e2' : '#fef3c7', color: t.urgentie >= 2 ? '#991b1b' : '#92400e' }} />
                    )}
                  </Box>
                ))}
              </Paper>
            )}
            {/* Quick Add */}
            <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: '0.75rem', border: '1px solid rgba(45,51,53,0.06)' }}>
              <TextField
                fullWidth
                placeholder="Nieuwe taak toevoegen..."
                value={quickAddText}
                onChange={(e) => setQuickAddText(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && quickAdd()}
                size="small"
                InputProps={{
                  startAdornment: <InputAdornment position="start"><AddIcon sx={{ color: '#526075' }} /></InputAdornment>,
                  endAdornment: quickAddText && (
                    <InputAdornment position="end">
                      <Button size="small" onClick={quickAdd} sx={{ textTransform: 'none', fontWeight: 600 }}>
                        Toevoegen
                      </Button>
                    </InputAdornment>
                  ),
                  sx: { borderRadius: 2 },
                }}
              />
            </Paper>

            {/* Today Drop Zone */}
            <DropZone id="today-zone">
              {loading.tasks ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress size={32} />
                </Box>
              ) : sortedActive.length === 0 && completedTodayTasks.length === 0 ? (
                <Paper elevation={0} sx={{ p: 4, textAlign: 'center', borderRadius: '0.75rem', border: '1px dashed rgba(45,51,53,0.12)' }}>
                  <TodayIcon sx={{ fontSize: 48, color: '#d1d5db', mb: 1 }} />
                  <Typography variant="body1" sx={{ color: '#767c7e', fontWeight: 500 }}>
                    Nog geen taken voor vandaag
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#adb3b5', mt: 0.5 }}>
                    Sleep taken hierheen of voeg er een toe
                  </Typography>
                </Paper>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {/* Active tasks */}
                  {sortedActive.map(item => (
                    item.isCalendarOnly
                      ? <CalendarOnlyCard key={item.id} item={item} />
                      : <DraggableCard key={item.id} item={item} onToggle={toggleComplete} onMoveDay={moveToTomorrow} moveDayDirection="tomorrow" onClickTask={handleClickTask} onOpenDatePicker={openDatePicker} />
                  ))}

                  {/* Completed section */}
                  {completedTodayTasks.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Box
                        onClick={() => setShowCompleted(!showCompleted)}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer',
                          p: 1, borderRadius: 1.5, '&:hover': { backgroundColor: '#f9fafb' },
                        }}
                      >
                        {showCompleted ? <ExpandLessIcon sx={{ fontSize: 18, color: '#adb3b5' }} /> : <ExpandMoreIcon sx={{ fontSize: 18, color: '#adb3b5' }} />}
                        <Typography variant="body2" sx={{ color: '#adb3b5', fontWeight: 500, fontSize: '0.8rem' }}>
                          Afgerond ({completedTodayTasks.length})
                        </Typography>
                      </Box>
                      <Collapse in={showCompleted}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5, pl: 1 }}>
                          {completedTodayTasks.map(item => (
                            <DraggableCard key={item.id} item={item} onToggle={toggleComplete} onClickTask={handleClickTask} onOpenDatePicker={openDatePicker} onMarkSent={markAsSent} />
                          ))}
                        </Box>
                      </Collapse>
                    </Box>
                  )}
                </Box>
              )}
            </DropZone>

            {/* Summary bar */}
            {todayTasks.length > 0 && (
              <Box sx={{
                mt: 2, p: 1.5, borderRadius: 2, backgroundColor: '#f9fafb',
                display: 'flex', gap: 2, justifyContent: 'center',
              }}>
                <Typography variant="caption" sx={{ color: '#767c7e' }}>
                  {activeTodayTasks.length} open
                </Typography>
                <Typography variant="caption" sx={{ color: '#767c7e' }}>·</Typography>
                <Typography variant="caption" sx={{ color: '#10b981', fontWeight: 600 }}>
                  {completedTodayTasks.length} afgerond
                </Typography>
                {totalHours > 0 && (
                  <>
                    <Typography variant="caption" sx={{ color: '#767c7e' }}>·</Typography>
                    <Typography variant="caption" sx={{ color: '#767c7e' }}>
                      {totalHours}u gepland
                    </Typography>
                  </>
                )}
              </Box>
            )}
          </Box>

          {/* ═══ MIDDLE: Tomorrow's Tasks (collapsible) ═══ */}
          <Box sx={{ flex: showTomorrow ? '1 1 30%' : '0 0 auto', minWidth: 0, transition: 'flex 0.3s ease' }}>
            <Paper elevation={0} sx={{ borderRadius: '0.75rem', border: '1px solid rgba(45,51,53,0.06)', overflow: 'hidden' }}>
              {/* Header — always visible, click to toggle */}
              <Box
                onClick={() => setShowTomorrow(!showTomorrow)}
                sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  p: 2, cursor: 'pointer', '&:hover': { backgroundColor: '#f1f4f5' },
                  borderBottom: showTomorrow ? '1px solid #f3f4f6' : 'none',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  {showTomorrow
                    ? <ExpandLessIcon sx={{ fontSize: 20, color: '#adb3b5' }} />
                    : <ExpandMoreIcon sx={{ fontSize: 20, color: '#adb3b5' }} />
                  }
                  <Box>
                    <Typography sx={{ fontWeight: 700, color: '#2d3335', fontSize: '1rem', lineHeight: 1.2 }}>
                      Morgen
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#adb3b5', fontSize: '0.7rem' }}>
                      {tomorrowLongDate()}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Chip
                    label={`${tomorrowTasks.length}`}
                    size="small"
                    sx={{ backgroundColor: '#fef3c7', color: '#d97706', fontWeight: 700, fontSize: '0.7rem', height: 22, minWidth: 28 }}
                  />
                  {totalHoursTomorrow > 0 && (
                    <Chip
                      icon={<ClockIcon sx={{ fontSize: 11 }} />}
                      label={`${totalHoursTomorrow}u`}
                      size="small"
                      sx={{ backgroundColor: '#f0fdf4', color: '#10b981', fontWeight: 600, fontSize: '0.65rem', height: 22 }}
                    />
                  )}
                </Box>
              </Box>

              {/* Collapsible content */}
              <Collapse in={showTomorrow}>
                <Box sx={{ p: 1.5 }}>
                  <DropZone id="tomorrow-zone">
                    {loading.tasks ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : sortedTomorrow.length === 0 ? (
                      <Box sx={{ py: 3, textAlign: 'center' }}>
                        <Typography variant="body2" sx={{ color: '#adb3b5', fontSize: '0.85rem' }}>
                          Nog geen taken voor morgen
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#d1d5db' }}>
                          Sleep taken hierheen of gebruik →
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {sortedTomorrow.map(item => (
                          item.isCalendarOnly
                            ? <CalendarOnlyCard key={item.id} item={item} />
                            : <DraggableCard key={item.id} item={item} onToggle={toggleComplete} onMoveDay={moveFromTomorrowToToday} moveDayDirection="today" onClickTask={handleClickTask} onOpenDatePicker={openDatePicker} />
                        ))}
                      </Box>
                    )}
                  </DropZone>
                </Box>
              </Collapse>
            </Paper>
          </Box>

          {/* ═══ RIGHT: Source Panel ═══ */}
          <Box sx={{ flex: '0 0 30%', minWidth: 280, maxWidth: 420 }}>
            <Paper elevation={0} sx={{ borderRadius: '0.75rem', border: '1px solid rgba(45,51,53,0.06)', overflow: 'hidden' }}>
              {/* Tabs */}
              <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                variant="fullWidth"
                sx={{
                  borderBottom: '1px solid #e5e7eb',
                  '& .MuiTab-root': { textTransform: 'none', fontWeight: 500, fontSize: '0.8rem', minHeight: 48, gap: 0.5 },
                }}
              >
                <Tab
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TaskIcon sx={{ fontSize: 18 }} />
                      Backlog
                      {backlogTasks.length > 0 && (
                        <Chip label={backlogTasks.length > 99 ? '99+' : backlogTasks.length} size="small"
                          sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, backgroundColor: '#d5e3fd', color: '#526075' }} />
                      )}
                    </Box>
                  }
                />
                <Tab
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TodoIcon sx={{ fontSize: 18 }} />
                      To-Do
                      {msTodos.length > 0 && (
                        <Chip label={msTodos.length > 99 ? '99+' : msTodos.length} size="small"
                          sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, backgroundColor: '#dbeafe', color: '#3b82f6' }} />
                      )}
                    </Box>
                  }
                />
                <Tab
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <NoteIcon sx={{ fontSize: 18 }} />
                      Acties
                      {actionItems.length > 0 && (
                        <Chip label={actionItems.length > 99 ? '99+' : actionItems.length} size="small"
                          sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, backgroundColor: '#fef3c7', color: '#d97706' }} />
                      )}
                    </Box>
                  }
                />
              </Tabs>

              {/* Search */}
              <Box sx={{ p: 1.5, borderBottom: '1px solid #f3f4f6' }}>
                <TextField
                  fullWidth
                  placeholder="Zoeken..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  size="small"
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#adb3b5' }} /></InputAdornment>,
                    sx: { borderRadius: 2, fontSize: '0.85rem', height: 36 },
                  }}
                />
              </Box>

              {/* Tab content */}
              <Box sx={{ maxHeight: 'calc(100vh - 340px)', overflowY: 'auto', p: 1.5 }}>

                {/* Backlog Tab */}
                {activeTab === 0 && (
                  <>
                  <TextField
                    fullWidth
                    placeholder="+ Nieuwe taak in backlog..."
                    value={backlogQuickAdd}
                    onChange={(e) => setBacklogQuickAdd(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && quickAddBacklog()}
                    size="small"
                    sx={{ mb: 1.5 }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><AddIcon sx={{ fontSize: 18, color: '#6366f1' }} /></InputAdornment>,
                      sx: { borderRadius: 2, fontSize: '0.85rem', height: 36 },
                    }}
                  />
                  {loading.tasks ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
                  ) : filteredBacklog.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 3 }}>
                      <Typography variant="body2" sx={{ color: '#adb3b5', fontSize: '0.85rem' }}>
                        Geen openstaande taken
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#d1d5db', display: 'block', mt: 0.5, fontSize: '0.75rem' }}>
                        Backlog toont je Airtable taken die niet voor vandaag of morgen gepland staan
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      {groupedBacklog.overdue.length > 0 && (
                        <>
                          <Typography variant="caption" sx={{ color: '#ef4444', fontWeight: 600, px: 0.5, mt: 0.5 }}>
                            Achterstallig ({groupedBacklog.overdue.length})
                          </Typography>
                          {groupedBacklog.overdue.map(item => <SourceItem key={item.id} item={item} onAdd={moveToToday} onAddToDate={openDatePicker} />)}
                        </>
                      )}
                      {groupedBacklog.thisWeek.length > 0 && (
                        <>
                          <Typography variant="caption" sx={{ color: '#f59e0b', fontWeight: 600, px: 0.5, mt: 0.5 }}>
                            Deze week ({groupedBacklog.thisWeek.length})
                          </Typography>
                          {groupedBacklog.thisWeek.map(item => <SourceItem key={item.id} item={item} onAdd={moveToToday} onAddToDate={openDatePicker} />)}
                        </>
                      )}
                      {groupedBacklog.nextWeek.length > 0 && (
                        <>
                          <Typography variant="caption" sx={{ color: '#767c7e', fontWeight: 600, px: 0.5, mt: 0.5 }}>
                            Volgende week ({groupedBacklog.nextWeek.length})
                          </Typography>
                          {groupedBacklog.nextWeek.map(item => <SourceItem key={item.id} item={item} onAdd={moveToToday} onAddToDate={openDatePicker} />)}
                        </>
                      )}
                      {groupedBacklog.rest.length > 0 && (
                        <>
                          <Typography variant="caption" sx={{ color: '#767c7e', fontWeight: 600, px: 0.5, mt: 0.5 }}>
                            Later ({groupedBacklog.rest.length})
                          </Typography>
                          {groupedBacklog.rest.map(item => <SourceItem key={item.id} item={item} onAdd={moveToToday} onAddToDate={openDatePicker} />)}
                        </>
                      )}
                      {groupedBacklog.noDate.length > 0 && (
                        <>
                          <Typography variant="caption" sx={{ color: '#adb3b5', fontWeight: 600, px: 0.5, mt: 0.5 }}>
                            Geen datum ({groupedBacklog.noDate.length})
                          </Typography>
                          {groupedBacklog.noDate.map(item => <SourceItem key={item.id} item={item} onAdd={moveToToday} onAddToDate={openDatePicker} />)}
                        </>
                      )}
                    </Box>
                  )}
                  </>
                )}

                {/* MS To-Do Tab */}
                {activeTab === 1 && (
                  <>
                  <TextField
                    fullWidth
                    placeholder="+ Nieuwe Microsoft To-Do..."
                    value={todoQuickAdd}
                    onChange={(e) => setTodoQuickAdd(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && quickAddTodo()}
                    size="small"
                    disabled={!msListId}
                    sx={{ mb: 1.5 }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><AddIcon sx={{ fontSize: 18, color: '#3b82f6' }} /></InputAdornment>,
                      sx: { borderRadius: 2, fontSize: '0.85rem', height: 36 },
                    }}
                  />
                  {loading.mstodo ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
                  ) : filteredMsTodos.length === 0 ? (
                    <Typography variant="body2" sx={{ color: '#adb3b5', textAlign: 'center', py: 3, fontSize: '0.85rem' }}>
                      Geen Microsoft To-Do taken
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      {filteredMsTodos.map(item => <SourceItem key={item.id} item={item} onAdd={moveToToday} onAddToDate={openDatePicker} />)}
                    </Box>
                  )}
                  </>
                )}

                {/* Action Items Tab */}
                {activeTab === 2 && (
                  loading.actions ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
                  ) : filteredActions.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 3 }}>
                      <Typography variant="body2" sx={{ color: '#adb3b5', fontSize: '0.85rem' }}>
                        Geen actiepunten gevonden
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#d1d5db', display: 'block', mt: 0.5, fontSize: '0.75rem' }}>
                        Acties worden automatisch uit vergadernotulen gehaald wanneer je naam erbij staat
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      {filteredActions.map(item => <SourceItem key={item.id} item={item} onAdd={moveToToday} onAddToDate={openDatePicker} />)}
                    </Box>
                  )
                )}
              </Box>
            </Paper>
          </Box>
        </Box>
      </Box>

      {/* Drag Overlay */}
      <DragOverlay>
        {dragActive && <DraggableCard item={dragActive} isOverlay />}
      </DragOverlay>

      {/* ═══ Task Detail Popover ═══ */}
      <Popover
        open={Boolean(taskAnchor) && Boolean(selectedTask)}
        anchorEl={taskAnchor}
        onClose={() => { setTaskAnchor(null); setSelectedTask(null); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { p: 2.5, maxWidth: 380, borderRadius: 2.5, minWidth: 300 } } }}
      >
        {selectedTask && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#2d3335', flex: 1, mr: 1 }}>
                {selectedTask.title}
              </Typography>
              <IconButton size="small" onClick={() => { setTaskAnchor(null); setSelectedTask(null); }} sx={{ p: 0.25 }}>
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>

            {selectedTask.projectName && (
              <Typography sx={{ fontSize: '0.8rem', color: '#767c7e', mb: 1 }}>
                📁 {selectedTask.projectName}
              </Typography>
            )}

            {/* Priority chips */}
            <Box sx={{ mb: 1.5 }}>
              <Typography sx={{ fontSize: '0.7rem', color: '#adb3b5', mb: 0.5 }}>Status/Prioriteit</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {['Prio 1', 'Prio 2', 'Prio 3', 'Geen prio', 'Wacht op feedback'].map(s => (
                  <Chip
                    key={s}
                    label={s}
                    size="small"
                    variant={selectedTask.priority === s ? 'filled' : 'outlined'}
                    sx={{
                      fontSize: '0.65rem', height: 24, cursor: 'pointer',
                      ...(selectedTask.priority === s
                        ? { bgcolor: PRIO_COLORS[s] || '#526075', color: '#fff' }
                        : { borderColor: '#e5e7eb' }),
                    }}
                    onClick={() => {
                      updateTaskField(selectedTask, { PrioNr: s });
                      setSelectedTask(prev => ({ ...prev, priority: s }));
                    }}
                  />
                ))}
              </Box>
            </Box>

            {/* Hours */}
            <Box sx={{ mb: 1.5 }}>
              <Typography sx={{ fontSize: '0.7rem', color: '#adb3b5', mb: 0.5 }}>Uren</Typography>
              <TextField
                size="small"
                type="number"
                value={selectedTask.hours || ''}
                onChange={(e) => setSelectedTask(prev => ({ ...prev, hours: e.target.value }))}
                onBlur={(e) => {
                  const val = parseFloat(e.target.value);
                  if (!isNaN(val)) updateTaskField(selectedTask, { UrenWerk: val });
                }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">uur</InputAdornment>,
                  sx: { fontSize: '0.85rem', height: 36 },
                }}
                sx={{ width: 120 }}
              />
            </Box>

            {/* Notes/Opmerkingen */}
            <Box sx={{ mb: 1.5 }}>
              <Typography sx={{ fontSize: '0.7rem', color: '#adb3b5', mb: 0.5 }}>Opmerkingen</Typography>
              <TextField
                size="small"
                multiline
                rows={3}
                fullWidth
                value={editingNotes}
                onChange={(e) => setEditingNotes(e.target.value)}
                onBlur={() => {
                  if (editingNotes !== (selectedTask.notes || '')) {
                    updateTaskField(selectedTask, { Opmerking: editingNotes });
                    setSelectedTask(prev => ({ ...prev, notes: editingNotes }));
                  }
                }}
                placeholder="Voeg opmerkingen toe..."
                InputProps={{ sx: { fontSize: '0.85rem' } }}
              />
            </Box>

            {/* Date */}
            <Box sx={{ mb: 1.5 }}>
              <Typography sx={{ fontSize: '0.7rem', color: '#adb3b5', mb: 0.5 }}>Datum</Typography>
              <TextField
                size="small"
                type="date"
                value={selectedTask.startDate || ''}
                onChange={(e) => {
                  const newDate = e.target.value;
                  moveToDate(selectedTask, newDate);
                  setSelectedTask(prev => ({ ...prev, startDate: newDate }));
                }}
                InputProps={{ sx: { fontSize: '0.85rem', height: 36 } }}
                sx={{ width: 160 }}
              />
            </Box>

            {/* Actions */}
            {selectedTask.projectId && (
              <Button
                size="small"
                variant="outlined"
                fullWidth
                onClick={() => { navigate(`/projects/${selectedTask.projectId}`); setTaskAnchor(null); setSelectedTask(null); }}
                sx={{ textTransform: 'none', fontSize: '0.8rem' }}
              >
                Ga naar project →
              </Button>
            )}
          </Box>
        )}
      </Popover>

      {/* ═══ Date Picker Popover ═══ */}
      <Popover
        open={Boolean(datePickerAnchor)}
        anchorEl={datePickerAnchor}
        onClose={() => { setDatePickerAnchor(null); setDatePickerItem(null); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        slotProps={{ paper: { sx: { p: 2, borderRadius: 2 } } }}
      >
        <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', mb: 1 }}>
          Verplaats naar:
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, mb: 1.5 }}>
          <Chip label="Vandaag" size="small" sx={{ cursor: 'pointer', fontSize: '0.75rem' }}
            onClick={() => { moveToDate(datePickerItem, todayStr()); setDatePickerAnchor(null); setDatePickerItem(null); }} />
          <Chip label="Morgen" size="small" sx={{ cursor: 'pointer', fontSize: '0.75rem' }}
            onClick={() => { moveToDate(datePickerItem, tomorrowStr()); setDatePickerAnchor(null); setDatePickerItem(null); }} />
        </Box>
        <TextField
          size="small"
          type="date"
          value={datePickerValue}
          onChange={(e) => setDatePickerValue(e.target.value)}
          InputProps={{ sx: { fontSize: '0.85rem', height: 36 } }}
          sx={{ mb: 1 }}
        />
        <Button
          size="small"
          variant="contained"
          fullWidth
          onClick={handleDatePickerConfirm}
          sx={{ textTransform: 'none', fontSize: '0.8rem' }}
        >
          Verplaatsen
        </Button>
      </Popover>
    </DndContext>
  );
}
